import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'

// Components
import LoadingSpinner from './components/ui/LoadingSpinner'
import ProtectedRoute from './components/auth/ProtectedRoute'
import PublicRoute from './components/auth/PublicRoute'
import AdminRoute from './components/auth/AdminRoute'

// Pages
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import DashboardPage from './pages/DashboardPage'
import DocumentsPage from './pages/documents/DocumentsPage'
import DocumentDetailPage from './pages/documents/DocumentDetailPage'
import ReceiptReviewPage from './pages/documents/ReceiptReviewPage'
import UploadPage from './pages/documents/UploadPage'
import CategoriesPage from './pages/categories/CategoriesPage'
import ExpensesPage from './pages/budget/ExpensesPage'
import IncomePage from './pages/budget/IncomePage'
import BudgetsPage from './pages/budget/BudgetsPage'
import ReportsPage from './pages/budget/ReportsPage'
import GoalsPage from './pages/budget/GoalsPage'
import ProfilePage from './pages/profile/ProfilePage'
import SettingsPage from './pages/settings/SettingsPage'
import AdminUsersPage from './pages/admin/UsersPage'
import AdminUserDetailPage from './pages/admin/UserDetailPage'
import NotFoundPage from './pages/NotFoundPage'

// Layout
import Layout from './components/layout/Layout'
import useTheme from './hooks/useTheme'
import useLanguage from './hooks/useLanguage'

function App() {
  const { isLoading, isAuthenticated } = useAuth()
  useTheme()
  useLanguage()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg)' }}>
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg)' }}>
      <Routes>
        {/* Landing page (public) */}
        <Route path="/" element={<LandingPage />} />

        {/* Public routes (only accessible when not authenticated) */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />

        {/* Protected routes (only accessible when authenticated) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route index element={<DashboardPage />} />

          {/* Documents */}
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="documents/upload" element={<UploadPage />} />
          <Route path="documents/:id" element={<DocumentDetailPage />} />
          <Route path="documents/:id/review" element={<ReceiptReviewPage />} />

          {/* Categories */}
          <Route path="categories" element={<CategoriesPage />} />

          {/* Budget */}
          <Route path="budget/expenses" element={<ExpensesPage />} />
          <Route path="budget/incomes" element={<IncomePage />} />
          <Route path="budget/budgets" element={<BudgetsPage />} />
          <Route path="budget/reports" element={<ReportsPage />} />
          <Route path="budget/goals" element={<GoalsPage />} />

          {/* Profile & Settings */}
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />

          {/* Admin */}
          <Route path="admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="admin/users/:id" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
        </Route>

        {/* Legacy redirects */}
        <Route path="/documents/*" element={<Navigate to="/dashboard/documents" replace />} />
        <Route path="/categories" element={<Navigate to="/dashboard/categories" replace />} />
        <Route path="/budget/*" element={<Navigate to="/dashboard/budget/expenses" replace />} />
        <Route path="/expenses" element={<Navigate to="/dashboard/budget/expenses" replace />} />
        <Route path="/profile" element={<Navigate to="/dashboard/profile" replace />} />
        <Route path="/settings" element={<Navigate to="/dashboard/settings" replace />} />
        <Route path="/admin/*" element={<Navigate to="/dashboard/admin/users" replace />} />

        {/* 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  )
}

export default App
